import { CardHolder } from 'store/services/cards/holders/cardHoldersApi';
import { OfferBuilderSeed, OfferLineItem } from 'types/interfaces/tradeOffer';

/**
 * Pure logic behind the counterparty picker. No React, no network — the modal
 * and the one-holder shortcut in CardActionMenu both drive off these functions,
 * so the two paths cannot disagree about which copy a pick resolves to.
 *
 * The governing rule: PICKER ROWS ARE PEOPLE, NOT COPIES. The holders endpoint
 * returns one entry per offerable copy, so everything here starts by grouping
 * those entries by userId.
 */

/** Best-first. Explicit ranks, not enum ordinal — ordinal is fragile under schema edits. */
const CONDITION_RANK: Record<string, number> = {
  NEAR_MINT: 0,
  SLIGHTLY_PLAYED: 1,
  MODERATELY_PLAYED: 2,
  HEAVILY_PLAYED: 3,
  DAMAGED: 4,
};

/** Unknown conditions sort LAST so an unrecognised enum never takes the
 *  default-selected top slot and get silently picked as somebody's best copy. */
export const rankOf = (condition: string): number =>
  CONDITION_RANK[condition] ?? 99;

/** One picker row: one PERSON, plus the copy a pick would seed. */
export interface HolderRow {
  /** The auto-selected best copy — what seeds the offer, and what the row displays. */
  holder: CardHolder;
  /** Offerable copies this person has. Drives the quiet "N copies" indicator when > 1. */
  copyCount: number;
}

/**
 * Best-condition copy, tiebreaking on lowest points — the copy a pick seeds.
 *
 * Lowest points on a tie is deliberate: at equal condition the cheaper copy is
 * the easier trade to balance, and it is the one the viewer is most likely to
 * be able to afford.
 */
const bestCopy = (a: CardHolder, b: CardHolder): CardHolder => {
  const ra = rankOf(a.condition);
  const rb = rankOf(b.condition);
  if (ra !== rb) return ra < rb ? a : b;
  if (a.quotedPricePoints !== b.quotedPricePoints) {
    return a.quotedPricePoints < b.quotedPricePoints ? a : b;
  }
  // Fully deterministic: identical condition AND price must still resolve the
  // same way on every render, or the seeded have row would vary between clicks.
  return a.userMTGCardHaveId <= b.userMTGCardHaveId ? a : b;
};

/**
 * Collapse copies into one row per holder, preserving the endpoint's ordering.
 *
 * `copyCount` counts only what the endpoint returned, which is already the
 * offerable set — every returned copy is priceable, for trade, and not the
 * viewer's own. So "2 copies" means two copies that can actually be offered
 * for, never a raw inventory number.
 */
export const groupHoldersByUser = (holders: CardHolder[]): HolderRow[] => {
  const byUser = new Map<number, HolderRow>();
  for (const holder of holders) {
    const existing = byUser.get(holder.userId);
    if (!existing) {
      byUser.set(holder.userId, { holder, copyCount: 1 });
    } else {
      byUser.set(holder.userId, {
        holder: bestCopy(existing.holder, holder),
        copyCount: existing.copyCount + 1,
      });
    }
  }
  return Array.from(byUser.values());
};

/**
 * Map an offerable copy onto a builder line item.
 *
 * The `?? ''` coercions are mandatory, not defensive noise. The holders wire
 * types setCode/setName/collectorNumber as nullable, but OfferCard declares them
 * non-null and OfferCardRow does `card.setCode.toUpperCase()` unguarded — a null
 * would throw INSIDE the offer builder, one navigation away from this code, and
 * present as a blank crashed page with no way back.
 *
 * quantity 1 with ownedQuantity as the stepper cap matches the shipped one-line
 * seed on the profile have-list hover.
 */
export const holderToLineItem = (holder: CardHolder): OfferLineItem => ({
  userMTGCardHaveId: holder.userMTGCardHaveId,
  quantity: 1,
  ownedQuantity: holder.ownedQuantity,
  unitPricePoints: holder.quotedPricePoints,
  foil: holder.foil,
  condition: holder.condition,
  card: {
    id: holder.card.id,
    oracleId: holder.card.oracleId,
    scryfallId: holder.card.scryfallId,
    name: holder.card.name,
    setCode: holder.card.setCode ?? '',
    setName: holder.card.setName ?? '',
    collectorNumber: holder.card.collectorNumber ?? '',
    lang: holder.card.lang,
    imageUrl: holder.card.imageUrl,
  },
});

/**
 * Seed for a picked holder: their card on THEIR side, nothing on ours.
 *
 * No suggestionId, deliberately. OfferBuilderContext REPLACES both sides when a
 * seed carries one and MERGES when it does not — so omitting it means an
 * in-progress offer with this counterparty gains a line rather than being wiped.
 *
 * counterparty.id is load-bearing: the context drops any seed whose id does not
 * match the profile resolved from the username in the route.
 */
export const holderToSeed = (holder: CardHolder): OfferBuilderSeed => ({
  counterparty: { id: holder.userId, username: holder.username },
  youSend: [],
  theySend: [holderToLineItem(holder)],
});

/**
 * `balance` replaced the old `points` sort, which ranked by the CARD's price.
 * Every row is the same printing, so that value barely varied — and where it did
 * it just duplicated Best condition, since the best copy is also the dearest.
 * The key is renamed rather than reused so a stale persisted 'points' cannot
 * silently resolve to the new meaning.
 */
export const HOLDER_SORTS = ['condition', 'trades', 'balance'] as const;
export type HolderSortKey = (typeof HOLDER_SORTS)[number];

export const HOLDER_SORT_LABELS: Record<HolderSortKey, string> = {
  condition: 'Best condition',
  trades: 'Most trades',
  balance: 'Most points',
};

/**
 * Row comparators, one per sort control option.
 *
 * Every one ends on userId so equal rows never reshuffle between renders —
 * Array.prototype.sort is not required to be stable across engines for equal
 * elements, and a visibly shuffling list reads as a bug.
 */
export const HOLDER_COMPARATORS: Record<
  HolderSortKey,
  (a: HolderRow, b: HolderRow) => number
> = {
  condition: (a, b) =>
    rankOf(a.holder.condition) - rankOf(b.holder.condition) ||
    b.holder.completedTradeCount - a.holder.completedTradeCount ||
    a.holder.quotedPricePoints - b.holder.quotedPricePoints ||
    a.holder.userId - b.holder.userId,

  trades: (a, b) =>
    b.holder.completedTradeCount - a.holder.completedTradeCount ||
    rankOf(a.holder.condition) - rankOf(b.holder.condition) ||
    a.holder.userId - b.holder.userId,

  /**
   * Most points: the holder's own balance, richest first — who can actually fund
   * a trade, which is the question this sort exists to answer.
   *
   * The `?? 0` keeps the comparator total for its own unit tests; in the UI this
   * branch is unreachable, because the sort button is not rendered at all until
   * every row carries a balance.
   */
  balance: (a, b) =>
    (b.holder.pointsBalance ?? 0) - (a.holder.pointsBalance ?? 0) ||
    b.holder.completedTradeCount - a.holder.completedTradeCount ||
    rankOf(a.holder.condition) - rankOf(b.holder.condition) ||
    a.holder.userId - b.holder.userId,
};

/** Sorts a COPY: RTK Query freezes cached arrays, so an in-place sort throws. */
export const sortHolderRows = (rows: HolderRow[], sortKey: HolderSortKey): HolderRow[] =>
  [...rows].sort(HOLDER_COMPARATORS[sortKey]);

/** "NEAR_MINT" → "NM". Falls back to the raw value rather than hiding it. */
const CONDITION_SHORT: Record<string, string> = {
  NEAR_MINT: 'NM',
  SLIGHTLY_PLAYED: 'LP',
  MODERATELY_PLAYED: 'MP',
  HEAVILY_PLAYED: 'HP',
  DAMAGED: 'DMG',
};

export const shortCondition = (condition: string): string =>
  CONDITION_SHORT[condition] ?? condition;

/**
 * Activity bucket → display label. `dormant` is the API value and "inactive" is
 * the UI wording; null (no recorded login) renders nothing at all rather than
 * guessing.
 */
export const activityLabel = (activity: CardHolder['activity']): string | null => {
  switch (activity) {
    case 'today':
      return 'active today';
    case 'this_week':
      return 'active this week';
    case 'this_month':
      return 'active this month';
    case 'dormant':
      return 'inactive';
    default:
      return null;
  }
};
